<template>
    <BCard
        no-body
        v-bind="$attrs"
    >
        <div
            class="collapsible-card__header"
            :class="{ 'collapsible-card__header--expanded': !isCollapsed }"
        >
            <div class="collapsible-card__title">
                <slot name="title" />
            </div>

            <div class="collapsible-card__actions">
                <slot
                    name="actions"
                    :is-collapsed="isCollapsed"
                />

                <BButton
                    class="collapsible-card__toggle"
                    type="button"
                    variant="flat-secondary"
                    size="sm"
                    :aria-controls="collapseId"
                    :aria-expanded="String(!isCollapsed)"
                    :aria-label="toggleLabel"
                    :title="toggleLabel"
                    @click="toggle"
                >
                    <FeatherIcon
                        :icon="isCollapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
                        size="18"
                    />
                </BButton>
            </div>
        </div>

        <BCollapse
            :id="collapseId"
            :visible="!isCollapsed"
        >
            <div :class="bodyClass">
                <slot />
            </div>
        </BCollapse>
    </BCard>
</template>

<script>
import { BButton, BCard, BCollapse } from 'bootstrap-vue'

let nextCollapseId = 1

export default {
    name: 'CollapsibleCard',
    components: {
        BButton,
        BCard,
        BCollapse,
    },
    inheritAttrs: false,
    props: {
        defaultCollapsed: {
            type: Boolean,
            default: false,
        },
        collapsed: {
            type: Boolean,
            default: null,
        },
        bodyClass: {
            type: [String, Array, Object],
            default: '',
        },
        collapseLabel: {
            type: String,
            default: null,
        },
        expandLabel: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            collapseId: `collapsible-card-${nextCollapseId++}`,
            internalCollapsed: this.defaultCollapsed,
        }
    },
    computed: {
        isCollapsed() {
            return typeof this.collapsed === 'boolean'
                ? this.collapsed
                : this.internalCollapsed
        },
        toggleLabel() {
            if (this.isCollapsed) {
                return this.expandLabel || this.translate(
                    'Component(CollapsibleCard):expand',
                    'Expand section',
                )
            }

            return this.collapseLabel || this.translate(
                'Component(CollapsibleCard):collapse',
                'Collapse section',
            )
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = typeof this.$t === 'function' ? this.$t(key) : key
            return translation && translation !== key ? translation : fallback
        },
        toggle() {
            const isCollapsed = !this.isCollapsed

            this.internalCollapsed = isCollapsed
            this.$emit('update:collapsed', isCollapsed)
            this.$emit('toggle', { isCollapsed })
        },
    },
}
</script>

<style lang="scss" scoped>
.collapsible-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 4rem;
    padding: 1rem;
}

.collapsible-card__header--expanded {
    border-bottom: 1px solid #ebe9f1;
}

.collapsible-card__title {
    min-width: 0;
}

.collapsible-card__actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    margin-left: 1rem;
}

.collapsible-card__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: .25rem;
}
</style>
